import { Field, FieldProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import { Locale } from '@synergy/models';
import { PoolTextField, PoolTypography } from '@synergy/components';
import { fields } from '@synergy/utils';
import { paloPlainText } from '@synergy/components-palo';

import { PaloPrintCount } from '../../../../../../common/components/palo/palo-print-count';
import { ProductDetailOverviewFormValues } from '../models/product-detail-overview-form-values';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

/** What the price information prints of an article name (#2225). */
const NAME_PRINT_LIMIT = 55;

export interface ProductDetailOverviewSectionBasicProps {
    selectedLanguage: Locale;
}

export const ProductDetailOverviewSectionBasic = (props: ProductDetailOverviewSectionBasicProps) => {
    const fieldNames = fields<ProductDetailOverviewFormValues>();

    /**
     * An article number belongs to one supplier only. Saving used to be refused
     * without saying why, so the number stays marked until it is changed
     * (#2117).
     */
    const takenNumber = useAppSelector(state => state.product.detail.overview.takenNumber);

    return (
        <>
            <PoolTypography styleType="sectionTitle" id="app.common.basicInfos" />

            <Grid container={true} spacing={2}>
                <Grid item={true} xs={12}>
                    <Field name={`name.${props.selectedLanguage}`}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                value={field.value ?? ''}
                                label={<FormattedMessage id="app.label.productName" />}
                                error={meta.touched && !!meta.error}
                                required={true}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                            />
                        )}
                    </Field>
                    {/* The price information cuts the name at 55 characters,
                        which the free-article dialog has always said and the
                        master record never did (#2225). */}
                    <Field name={`name.${props.selectedLanguage}`}>
                        {({ field }: FieldProps<string>) => (
                            <PaloPrintCount
                                length={paloPlainText(field.value).length}
                                limit={NAME_PRINT_LIMIT}
                                cutBy="priceInformation"
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.manufacturer}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.manufacturer" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.manufacturerNumber}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.manufacturerNumber" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.brand}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.brand" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.number}>
                        {({ field, meta }: FieldProps<string>) => {
                            const taken = takenNumber?.number === field.value;

                            return (
                                <PoolTextField
                                    {...field}
                                    label={<FormattedMessage id="app.label.productNumber" />}
                                    required={true}
                                    error={taken || (meta.touched && !!meta.error)}
                                    helperText={
                                        taken ? (
                                            <FormattedMessage
                                                id="app.product.detail.numberTakenBySupplier"
                                                values={{ supplier: takenNumber?.supplier }}
                                            />
                                        ) : meta.touched && meta.error ? (
                                            <FormattedMessage id={meta.error} />
                                        ) : null
                                    }
                                    fullWidth={true}
                                />
                            );
                        }}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.globalTradeItemNumber}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                label={<FormattedMessage id="app.label.gtin" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                fullWidth={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.eclassPosition}>
                        {({ field }: FieldProps<number>) => (
                            <PoolTextField
                                value={field.value || '-'}
                                label={<FormattedMessage id="app.label.eclass" />}
                                fullWidth={true}
                                readOnly={true}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>
        </>
    );
};
